<template>
  <div>
    <div class="breadcrumb-copy-wrapper">
      <div class="breadcrumb">
        <h4 class="overview-text breadcrumb-first">
          <router-link :to="{ name: 'awsoverview' }" class="breadcrumb-link">
            {{ $t('aws.overviewText') }}
          </router-link>
        </h4>
        <h4 class="overview-text">
          {{ $route.params.subscriptionId }}
        </h4>
      </div>
    </div>
    <RexSpinV3 :spinning="isLoading">
      <div class="overview-tiles">
        <AWSTile :tiles="tilesData" :numberOfRows="1" />
      </div>
      <div class="drop-down-section">
        <h4>
          {{ $t('aws.selectInstances') }}
        </h4>
        <RexSelectV3
          mode="multiple"
          :maxTagTextLength="12"
          :maxTagCount="1"
          v-model:value="selectedCacheList"
          :options="computedInstances"
          @onChange="onOptionSelect"
          class="capability-dropdown"
          id="search-select-functions"
        >
          <template #default>
            <RexSelectOption
              v-for="obj in computedInstances"
              :key="obj.key"
              :value="obj.label"
            >
            </RexSelectOption>
          </template>
        </RexSelectV3>
      </div>
      <div class="subscription-graphs">
        <RexTabsV3
          :tabs="tabsStatus"
          @onTabChange="onTabChange"
          :activeKey="selectedOption"
        >
          <!-- Tab 1 Content -->
          <template #tab-1 v-if="selectedOption === '1'">
            <div class="graphs-wrapper">
              <div
                class="chart-element"
                v-for="(chartConfig, index) in getGraphTemplate"
                :key="index"
                :style="{ width: chartConfig.width }"
                :id="'graph-' + index"
              >
                <AwsMetricsGraph
                  showMoreOptions
                  :moreOptionsData="getMoreOptionsData(index, chartConfig)"
                  :graphConfig="chartConfig"
                  :index="index"
                  :label="graphLegends"
                />
              </div>
            </div>
          </template>

          <!-- Tab 2 Content -->
          <template #tab-2 v-if="selectedOption === '2'">
            <div class="graphs-wrapper">
              <div
                class="chart-element"
                v-for="(chartConfig, index) in getGraphTemplate"
                :key="index"
                :style="{ width: chartConfig.width }"
                :id="'graph-' + index"
              >
                <AwsMetricsGraph
                  showMoreOptions
                  :moreOptionsData="getMoreOptionsData(index, chartConfig)"
                  :graphConfig="chartConfig"
                  :index="index"
                  :label="graphLegends"
                />
              </div>
            </div>
          </template>

          <!-- Tab 2 Content -->
          <template #tab-3 v-if="selectedOption === '3'">
            <div class="graphs-wrapper">
              <div
                class="chart-element"
                v-for="(chartConfig, index) in getGraphTemplate"
                :key="index"
                :style="{ width: chartConfig.width }"
                :id="'graph-' + index"
              >
                <AwsMetricsGraph
                  showMoreOptions
                  :moreOptionsData="getMoreOptionsData(index, chartConfig)"
                  :graphConfig="chartConfig"
                  :index="index"
                  :label="graphLegends"
                />
              </div>
            </div>
          </template>
        </RexTabsV3>
        <CreateAlert
          :instancesProp="getInstancesForAlerts"
          :metricNameProp="metricNameForAddToDashboard"
          instancePropType="function"
          v-if="getShowAddConfigureAlert"
        />
        <AddToDashboard
          :chartConfig="addToDashboardChartConfig"
          :metric-name="metricNameForAddToDashboard"
          @onClose="() => (showAddToDashboard = false)"
          :showAddToDashboard="showAddToDashboard"
          :instances="getInstancesForAddToDashboard"
          v-if="showAddToDashboard"
        />
      </div>
    </RexSpinV3>
  </div>
</template>

<script setup lang="ts">
import cloudTemplate from '@/utils/templates/cloud.template';
import RexTabsV3 from '@/rex-components/RexTabsV3.vue';
import AWSTile from '@/components/Cloud/Dashboard/Components/aws-tiles.vue';
import RexSelectV3 from '@/rex-components/RexSelectV3.vue';
import {
  ingressSubscriptionHeader,
  tabs,
  tabMapper,
} from '@/components/Cloud/Dashboard/pages/ingress/ingress.config';
import AwsMetricsGraph from '@/components/Cloud/Dashboard/Components/aws-metrics-graph.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import CreateAlert from '@/components/AlertsV2/CreateAlert.vue';
import AddToDashboard from '@/components/Cloud/Dashboard/Components/add-to-dashboard.vue';
import { parseServiceObjFromApplication } from '@/utils/helper-function';
import { computed, onMounted, ref, watch } from 'vue';
import { useStoreData } from '@/components/Common/useStoreData';
import { useStoreDataCloud } from '@/components/Cloud/Dashboard/useStoreData';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMatricesData } from '@/components/Cloud/Hooks/useMetricsData';
import { Subscriptions } from '@/components/Cloud/Hooks/cloud.all.types';
import { useGetServiceInstances } from '@/components/Cloud/Hooks/useGetServiceInstances';
import { MIN_SLICE, SUBSCRIPTION_MAX_SLICE } from './ingress.config';
import ingressCharts from '@/utils/templates/cloudTemplates/aws/ingress.config.js';
import { usePermissions } from '@/hooks/usePermissions';
import {
  capabilityResourceGroupMap,
  CLOUD_RESOURCE_GROUP,
  HOME_RESOURCE_GROUP,
  permissionTypes,
} from '@/router/constant';

const { t } = useI18n();

// Store references
const store = useStore();
const route = useRoute();
const { params } = useRoute();
const { getDurationTime } = useStoreData();
const {
  setCurrentServiceInstance,
  setShowAddConfigureAlert,
  setCurrentAlertCapability,
  setCurrentAlertApplication,
  setAlertApplication,
  getShowAddConfigureAlert,
  setCurrentPage,
  getCurrentServiceInstances,
  getCurrentService,
  setCurrentSubType,
  setCurrentTab,
} = useStoreDataCloud();

const graphLegends = ref('');
const isLoading = ref(false);
const showAddToDashboard = ref(false);
const metricNameForAddToDashboard = ref('');
const addToDashboardChartConfig = ref({});
const selectedOption = ref('1');
const getInstancesForAlerts = ref<any[]>([]);
const subscriptions = ref<Subscriptions[]>([
  { key: params.serviceId as string },
]);

const { subscriptionMatrices, filteredSubscriptionMatrices, fetchMatrices } =
  useMatricesData({
    headers: ref(ingressSubscriptionHeader()),
    subscriptions,
  });

const { data: instances, fetchServiceInstances } = useGetServiceInstances({
  serviceId: params.serviceId,
});

const computedInstances = computed(() => {
  return instances.value
    ?.map((instance) => {
      return {
        ...instance,
        value: instance.key,
      };
    })
    .filter(
      (instance) => instance.instanceType === tabMapper[selectedOption.value],
    );
});

const getInstancesForAddToDashboard = computed(() => {
  return computedInstances.value
    .filter((instance) => {
      return getCurrentServiceInstances.value.includes(instance.value);
    })
    .map((instance) => ({
      keyLabel: instance.label,
      value: instance.value,
    }));
});

watch(computedInstances, (currentValue, previousValue) => {
  // if (previousValue === undefined) {
  selectedCacheList.value = currentValue
    .map((instance) => instance.key)
    .slice(0, 1);

  setCurrentServiceInstance(
    currentValue
      .map((instance) => instance.key)
      .slice(MIN_SLICE, SUBSCRIPTION_MAX_SLICE),
  );

  graphLegends.value = currentValue
    .filter((instance) => {
      return getCurrentServiceInstances.value.includes(instance.key);
    })
    .map((instance) => instance.label)
    .join(', ');
  // }
});
const tilesData = computed(() => {
  return ingressSubscriptionHeader()
    .map((item) => {
      if (!subscriptionMatrices.value[0]) {
        return undefined;
      }
      if (item.key in subscriptionMatrices.value[0]) {
        return {
          title: item.tileName,
          content: subscriptionMatrices.value[0][item.key],
          tooltip: item.tooltip,
        };
      }
    })
    .filter((item) => item);
});

const onTabChange = (key) => {
  setCurrentTab(tabs[key - 1].key);
  selectedOption.value = key;
};

const getMoreOptionsData = (graphIndex, chartConfig) => {
  const { hasCapabilityAlertWritePermission, hasCapabilityWritePermission } =
    usePermissions();

  const hasAlertWrite = hasCapabilityAlertWritePermission(
    capabilityResourceGroupMap[CLOUD_RESOURCE_GROUP],
  );

  const allowHome =
    hasCapabilityWritePermission(
      capabilityResourceGroupMap[HOME_RESOURCE_GROUP],
    ) &&
    hasCapabilityWritePermission(
      capabilityResourceGroupMap[CLOUD_RESOURCE_GROUP],
    );
  return [
    ...(allowHome
      ? [
          {
            id: 2,
            label: t('aws.addToDashboard'),
            callBack: (options) => {
              metricNameForAddToDashboard.value =
                getGraphTemplate.value?.[graphIndex].metricName || '';
              addToDashboardChartConfig.value = chartConfig;
              showAddToDashboard.value = true;
            },
          },
        ]
      : []),
    ...(hasAlertWrite
      ? [
          {
            id: 3,
            label: t('aws.createAlert'),
            callBack: (options) => {
              metricNameForAddToDashboard.value =
                getGraphTemplate.value?.[graphIndex].metricName || '';
              setCurrentSubType('ELB');
              setCurrentAlertCapability({
                key: 'CAPABILITY_CLOUD',
                label: 'Cloud monitoring',
              });
              const subscriptionId = parseServiceObjFromApplication({
                key: getCurrentService.value,
                label: '',
              }).name;
              const keyLabel = {
                key: params.serviceId,
                label: subscriptionId,
                permissions: permissionTypes.ALERT_WRITE,
              };
              setCurrentAlertApplication(keyLabel);
              setAlertApplication([keyLabel]);
              setShowAddConfigureAlert(true);
            },
          },
        ]
      : []),
    {
      id: 4,
      label: t('aws.downloadAsCsv'),
      callBack: (options) => {
        const headers = [
          t('aws.instanceName'),
          ...options.data.series.flatMap((option) =>
            option.data.map((item) => item.x?.replace('\n', ' ')),
          ),
        ];
        const headerSet = new Set(headers);
        const data = options.data.series.map((option) => [
          option.name,
          ...option.data.map((item) => item.y),
        ]);
        const rows = [
          [t('aws.metricName'), options.data.title],
          [t('aws.connectionName'), params.subscriptionId],
          [t('aws.startTime'), getDurationTime.value.end],
          [t('aws.endTime'), getDurationTime.value.start],
          [t('aws.resolution'), getDurationTime.value.step],
          Array.from(headerSet),
          ...data,
        ];
        const filename = `${options.data.title}.csv`;
        const csvContent = rows.map((e) => e.join(',')).join('\n');
        // Create a Blob from the CSV content
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        // Create an object URL for the Blob
        const url = URL.createObjectURL(blob);
        // Create a temporary `a` element
        const link = document.createElement('a');
        link.href = url;
        link.download = filename; // Set the desired file name
        // Programmatically trigger a click event on the link
        link.click();
        // Clean up by revoking the object URL
        URL.revokeObjectURL(url);
      },
    },
  ];
};

onMounted(async () => {
  fetchServiceInstances();
  fetchMatrices();
  setCurrentPage('subscriptionId');
});

watch(getDurationTime, () => {
  fetchMatrices();
});

const tabsStatus = computed(() => {
  const instanceTypeSet = new Set(
    instances.value?.map((instance) => instance.instanceType),
  );
  return tabs?.map((tab) => {
    return {
      ...tab,
      disabled: !Array.from(instanceTypeSet).includes(tab.key),
    };
  });
});

watch(instances, () => {
  const instanceTypeSet = new Set(
    instances.value?.map((instance) => instance.instanceType),
  );
  if (instanceTypeSet.has('ALB')) {
    return onTabChange('1');
  }
  if (instanceTypeSet.has('NLB')) {
    return onTabChange('2');
  }
  onTabChange('3');
});

const selectedCacheList = ref<string[]>([]);

const onOptionSelect = (value, options) => {
  if (options.length > 5) {
    return;
  }
  selectedCacheList.value = options.map((option) => option.value);
  graphLegends.value = options.map((option) => option.label).join(', ');
  setCurrentServiceInstance(selectedCacheList.value);
};

const getGraphTemplate = computed(() => {
  return ingressCharts()[`lineCharts${tabMapper[selectedOption.value]}`];
});

const getIngressInstancesForAlerts = () => {
  getInstancesForAlerts.value = computedInstances.value?.map((instance) => {
    const selected = selectedCacheList.value.includes(instance.key);
    return { key: instance.key, label: instance.label, selected };
  });
};
watch(getShowAddConfigureAlert, getIngressInstancesForAlerts);
</script>

<style lang="scss" scoped>
.breadcrumb-text {
  display: flex;
  margin-bottom: 1.25rem;
  color: $color-brand-accent;
}

.chart-container {
  @include flex-center-justify(space-between);
  flex-wrap: wrap;
}

.chart-element {
  margin-bottom: 1.25rem;
}

.subscription-graphs {
  @include flex-center-justify(start);
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-top: 1.25rem;
}

.graphs-wrapper {
  @include flex-center-justify(start);
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-top: 1.25rem;
}

.breadcrumb-first {
  cursor: pointer;
}

.breadcrumb {
  display: flex;
  background: inherit;
  gap: 0.3125rem;
}

.breadcrumb-link:hover {
  text-decoration: none;
  color: inherit;
  color: $color-brand-accent;
}

.breadcrumb-link {
  color: $color-brand-accent;
  text-decoration: none;
}

.breadcrumb-copy-wrapper {
  @include flex-center-justify(space-between);
}

.drop-down-section {
  @include flex-center-justify(start);
  gap: 0.625rem;
  margin-top: 0.875rem;
}
</style>
